import { resolveManufacturerFromParam } from '../../util/categoryManufacturerRouting';

export const ABSOLUTE_MAX_SEO_TITLE_LENGTH = 65;

export const getCategoryStoreSuffix = (configStore) =>
  `${configStore.store.name} ${configStore.languageTexts.TEXT_SEO_STORE_NAME_ADD}`.trim();

export const trimTitleSegment = (value, maxLength) => {
  if (!value || maxLength <= 0) {
    return '';
  }

  if (value.length <= maxLength) {
    return value;
  }

  if (maxLength <= 3) {
    return value.slice(0, maxLength);
  }

  return `${value.slice(0, maxLength - 3)}...`;
};

export const truncateTitleSegment = (value, maxLength) => {
  if (!value || maxLength <= 0) {
    return '';
  }

  if (value.length <= maxLength) {
    return value;
  }

  return trimTitleSegment(value, maxLength);
};

export const enforceMaxTitleLength = ({
  baseTitle,
  manufacturerName,
  storeSuffix,
  hasCustomSeoTitle,
}) => {
  const manufacturerPrefix = manufacturerName ? `${manufacturerName} - ` : '';
  const generatedStoreSuffix =
    !hasCustomSeoTitle && storeSuffix ? ` - ${storeSuffix}` : '';

  let title = `${manufacturerPrefix}${baseTitle}${generatedStoreSuffix}`;
  if (title.length <= ABSOLUTE_MAX_SEO_TITLE_LENGTH) {
    return title;
  }

  if (!hasCustomSeoTitle) {
    title = `${manufacturerPrefix}${baseTitle}`;
    if (title.length <= ABSOLUTE_MAX_SEO_TITLE_LENGTH) {
      return title;
    }

    return title.slice(0, ABSOLUTE_MAX_SEO_TITLE_LENGTH);
  }

  const allowedBaseLength =
    ABSOLUTE_MAX_SEO_TITLE_LENGTH - manufacturerPrefix.length;
  const trimmedBaseTitle = truncateTitleSegment(baseTitle, allowedBaseLength);
  const trimmedTitle = `${manufacturerPrefix}${trimmedBaseTitle}`;
  return trimmedTitle.slice(0, ABSOLUTE_MAX_SEO_TITLE_LENGTH);
};

export const getCategorySeoTitleData = ({
  activeCategory,
  configStore,
  manufacturerStore,
  manufacturerParam,
}) => {
  if (!activeCategory) {
    return {
      title: '',
      noSuffix: false,
    };
  }

  const manufacturer = resolveManufacturerFromParam(
    manufacturerStore,
    manufacturerParam
  );
  const baseTitle = activeCategory.SEO_title || activeCategory.name;
  if (!manufacturer) {
    if (hasCustomSeoTitle(activeCategory)) {
      return {
        title: truncateTitleSegment(baseTitle, ABSOLUTE_MAX_SEO_TITLE_LENGTH),
        noSuffix: true,
      };
    }

    const storeSuffix = configStore ? getCategoryStoreSuffix(configStore) : '';
    const fullTitle = storeSuffix ? `${baseTitle} - ${storeSuffix}` : baseTitle;

    return {
      title:
        fullTitle.length <= ABSOLUTE_MAX_SEO_TITLE_LENGTH
          ? baseTitle
          : baseTitle.slice(0, ABSOLUTE_MAX_SEO_TITLE_LENGTH),
      noSuffix: fullTitle.length > ABSOLUTE_MAX_SEO_TITLE_LENGTH,
    };
  }

  return {
    title: enforceMaxTitleLength({
      baseTitle,
      manufacturerName: manufacturer.name,
      storeSuffix: configStore ? getCategoryStoreSuffix(configStore) : '',
      hasCustomSeoTitle: Boolean(activeCategory.SEO_title),
    }),
    noSuffix: true,
  };
};

const hasCustomSeoTitle = (activeCategory) =>
  Boolean(activeCategory?.SEO_title);

export const buildCategoryDisplayName = ({
  activeCategory,
  manufacturerStore,
  manufacturerParam,
}) => {
  if (!activeCategory) {
    return '';
  }

  const manufacturer = resolveManufacturerFromParam(
    manufacturerStore,
    manufacturerParam
  );

  if (!manufacturer) {
    return activeCategory.name;
  }

  return `${manufacturer.name} - ${activeCategory.name}`;
};

export const buildCategorySeoTitle = (props) =>
  getCategorySeoTitleData(props).title;

export const shouldDisableCategorySeoTitleSuffix = (props) =>
  getCategorySeoTitleData(props).noSuffix;

export const buildFinalCategorySeoTitle = (props) => {
  const { configStore } = props;
  const { title, noSuffix } = getCategorySeoTitleData(props);

  if (!title) {
    return '';
  }

  if (noSuffix) {
    return title;
  }

  return `${title} - ${getCategoryStoreSuffix(configStore)}`;
};
